<template>
  <Page :breadcrumb="breadCrumb">
    <template v-if="isArticle" slot="primary-content">
      <Article :article="article" class="primary-content" />
      <RelatedArticles :articles="relatedArticles" />
    </template>
    <Category
      v-else
      slot="primary-content"
      class="primary-content"
      :category="category"
    />
  </Page>
</template>

<script>
import jsonldDS from '@itbpbg/lms-components/src/utils/json-ld-ds';
import jsonldFA from '@itbpbg/lms-components/src/utils/json-ld-fa';
import global from '@itbpbg/lms-components/src/mixins/global';
import Page from '../../components/HelpCenter/Page';
import Article from '../../components/HelpCenter/Article';
import RelatedArticles from '../../components/HelpCenter/RelatedArticles';
import Category from '../../components/HelpCenter/Category';

export default {
  components: {
    Page, Article, RelatedArticles, Category,
  },
  mixins: [global],
  async asyncData({
    $axios, route, error, store,
  }) {
    try {
      const tree = store.state.support.tree.length === 0;
      const response = await $axios.$get(`/help-center/${route.params.id}?${tree ? 'tree=true' : ''}`);

      if (response.tree.length) {
        store.commit('support/setArticlesTree', response.tree);
      }

      if (response.entity === 'category') {
        const page = await $axios.$get(`/pages/${encodeURIComponent(route.path.replace(/^\/|\/$/g, ''))}`);
        response.page = page;
      }

      return response;
    } catch (e) {
      return error({ title: 'Article not found', statusCode: 404, message: 'Article not found' });
    }
  },
  data: () => ({
    entity: null,
    category: {},
    article: {},
    relatedArticles: [],
  }),
  head() {
    const jsonld = this.$config.platform === 'ds' ? jsonldDS : jsonldFA;
    const seoPage = this.page ? this.page : this.article;
    const siteFullUrl = `${this.$config.siteUrl}${this.$route.fullPath}`;
    const readAction = jsonld.PotentialActions.consume.read;
    const searchAction = jsonld.PotentialActions.search;
    readAction.target = [siteFullUrl];
    searchAction.target = [`${this.$config.siteUrl}/support/search?q={search_term_string}`];

    let jsonData;

    const seoWebPage = {
      ...jsonld.WebPage,
      url: siteFullUrl,
      name: seoPage.metaTitle,
      description: seoPage.metaDescription,
      datePublished: this.dateIsoString(seoPage.created),
      dateModified: this.dateIsoString(seoPage.updated),
      potentialAction: [searchAction, readAction],
    };

    const seoWebSite = { ...jsonld.WebSite };

    const seoImageObject = {
      ...jsonld.ImageObject,
      '@id': seoPage.image,
      url: seoPage.metaImage ? `${this.$config.imagesUrl}/meta/${seoPage.metaImage}` : 'https://365datascience.com/resources/facebook_open_graph.png',
      width: seoPage.metaImageWidth,
      height: seoPage.metaImageHeight,
      caption: seoPage.title,
    };

    const breadcrumb = [{
      name: 'Help Center',
      slug: '/support/',
    },
    {
      name: seoPage.categoryName || this.category.name,
      slug: `/${seoPage.categorySlug || this.category.slug}/`,
    },
    ];

    if (this.isArticle) {
      breadcrumb.push({
        name: seoPage.title,
        slug: `/${seoPage.slug}/`,
      });

      const seoArticle = {
        ...jsonld.Article,
        '@id': siteFullUrl,
        isPartOf: siteFullUrl,
        mainEntityOfPage: siteFullUrl,
        headline: seoPage.title,
        keywords: seoPage.metaKeywords,
        image: {
          ...jsonld.Article.image,
          '@id': seoPage.image,
        },
        datePublished: this.dateIsoString(seoPage.created),
        dateModified: this.dateIsoString(seoPage.updated),
        potentialAction: [searchAction, readAction],
      };

      jsonData = [
        {
          '@context': 'https://schema.org',
          '@graph': [seoWebSite, seoImageObject, seoWebPage, seoArticle],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: jsonld.JSONBreadcrumb(breadcrumb, this.$config.siteUrl),
        },
      ];
    } else {
      jsonld.CollectionPage.mainEntity.itemListElement = [];
      if (this.category.articles) {
        this.category.articles.forEach((item) => {
          const post = {
            '@type': 'ItemPage',
            name: item.title,
          };
          jsonld.CollectionPage.mainEntity.itemListElement.push(post);
        });
      }

      jsonData = [
        {
          '@context': 'https://schema.org',
          '@graph': [seoWebSite, seoImageObject, seoWebPage, jsonld.CollectionPage],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: jsonld.JSONBreadcrumb(breadcrumb, this.$config.siteUrl),
        },
      ];
    }

    const title = `${this.title} – Help Center | 365 Data Science`;

    seoPage.metaTitle = this.isArticle && !seoPage.metaTitle ? `${this.title} - Help Center` : `${seoPage.metaTitle} - Help Center`;
    seoPage.metaDescription = this.isArticle && !seoPage.metaDescription ? `Need support? Find the answer to "${this.title}" and more in our Help Center. Visit now!` : seoPage.metaDescription;
    seoPage.fullPath = siteFullUrl;

    if (seoPage.robots) {
      if (seoPage.robots === 'noindex') {
        seoPage.robots = 'noindex, follow';
      } else if (seoPage.robots === 'nofollow') {
        seoPage.robots = 'index, nofollow';
      }
    } else {
      seoPage.robots = 'index, follow';
    }

    const meta = [
      ...this.metaProps(
        {
          ...seoPage,
          ogTitle: seoPage.ogTitle || seoPage.metaTitle || title,
          ogImage: seoPage.ogImage || seoPage.metaImage,
          ogDescription: seoPage.ogDescription || seoPage.metaDescription,
          twitterTitle: seoPage.twitterTitle || seoPage.metaTitle || title,
          twitterImage: seoPage.twitterImage || seoPage.metaImage,
          twitterDescription: seoPage.twitterDescription || seoPage.metaDescription,
          robots: `${seoPage.robots}, max-snippet:-1, max-image-preview:large, max-video-preview:-1`,
        },
      ),
    ];
    return {
      titleTemplate: title,
      link: [
        { rel: 'canonical', href: seoPage.canonicalURL || this.canonicalURL() },
      ],
      meta,
      script: jsonData ? [
        {
          vmid: 'jsonld',
          type: 'application/ld+json',
          json: jsonData,
        },
      ] : [],
    };
  },
  computed: {
    isArticle() {
      return this.entity === 'article';
    },
    title() {
      return this.article.title || this.page.title;
    },
    breadCrumb() {
      if (this.entity === 'category') {
        return [{ name: 'Help Center', slug: '/support/' }, { name: this.category.name, slug: '' }];
      }

      return [
        { name: 'Help Center', slug: '/support/' },
        { name: this.article.categoryName, slug: `/support/${this.article.categorySlug}/` },
        { name: this.article.title, slug: '' },
      ];
    },
  },
};
</script>

<style lang="scss">
.ds {
  .support-page {
    .breadcrumb ul li {
      a {
        text-transform: none;
      }
    }
  }
}

.fa {
  @import '../../assets/sass/fa/components/helpcenter.scss';
}
</style>
